import React, { useEffect, useState } from "react";
import api from "../../services/api";
import UserIcon from "../../assets/img/user.png";
import QrIcon from "../../assets/img/qr.png";
import DownloadIcon from "../../assets/img/download.png";
import DeleteIcon from "../../assets/img/delete.png";

const PeerCard = ({ peer, fetchPeers }) => {
  const deleteConfig = () => {
    var result = confirm(`Are you sure you want to delete peer ${peer?.name}?`);
    if (result) {
      async function deletePeer() {
        try {
          await api.post("/order/expire", {
            order_id: peer?.id.toString(),
          });
          await fetchPeers();
        } catch (error) {
          logger.error(`Delete peer ${peer?.id} failed!`);
        }
      }
      deletePeer();
    }
  };

  const downloadConfig = async (type) => {
    try {
      const response = await api.post(
        `/order/${type}`,
        {
          order_id: peer?.id.toString(),
        },
        {
          responseType: "blob",
        }
      );

      // Determine the file extension based on the type
      let fileExtension;
      let mimeType;
      switch (type) {
        case "file":
          fileExtension = ".conf";
          mimeType = "application/octet-stream";
          break;
        case "qr":
          fileExtension = ".png";
          mimeType = "image/png";
          break;
        default:
          fileExtension = ".png"; // Default to a text file if type is not recognized
          mimeType = "text/plain";
      }

      // Create a blob with specified type
      const blob = new Blob([response.data], { type: mimeType });

      // Create a URL for the blob
      const url = window.URL.createObjectURL(blob);

      // Create a temporary link element and click it to trigger the download
      const link = document.createElement("a");
      link.href = url;

      const fileName = `download${fileExtension}`;

      // Use Content-Disposition header to suggest filename
      link.setAttribute("download", fileName);
      link.setAttribute("target", "_blank"); // Open in a new tab
      link.setAttribute("rel", "noopener noreferrer"); // Security measure

      // Add Content-Disposition header
      link.setAttribute(
        "Content-Disposition",
        `attachment; filename="${fileName}"`
      );

      // Add link to the document body
      document.body.appendChild(link);

      // Simulate a click on the link
      link.click();

      // Clean up
      document.body.removeChild(link);
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <div className="bg-[#fdfdff] border-[#e3e3e3] border-2 rounded-lg p-4">
      <div className="flex flex-col justify-center items-center space-y-2">
        <img
          src={UserIcon}
          alt="User Icon"
          className="size-12 sm:size-14 md:size-16"
        />
        <div className="text-md sm:text-lg font-semibold text-gray-800">
          {peer?.name}
        </div>
      </div>
      <table className="mx-auto my-4 text-xs sm:text-sm md:text-md">
        <tbody>
          <tr>
            <td>
              <b>IP</b>
            </td>
            <td>:</td>
            <td>{peer?.ip}</td>
          </tr>
          <tr>
            <td className="pr-4">
              <b>Order Date</b>
            </td>
            <td className="pr-6">:</td>
            <td>{peer?.order_date}</td>
          </tr>
          <tr>
            <td>
              <b>Expiry Date</b>
            </td>
            <td>:</td>
            <td>
              {parseInt(peer?.expiry_date.split("-")[0]) > 2045
                ? "No Expiry"
                : peer?.expiry_date}
            </td>
          </tr>
          <tr>
            <td>
              <b>Endpoint</b>
            </td>
            <td>:</td>
            <td>{peer?.endpoint}</td>
          </tr>
          <tr>
            <td>
              <b>Handshake</b>
            </td>
            <td>:</td>
            <td>{peer?.handshake}</td>
          </tr>
          <tr>
            <td>
              <b>Tx</b>
            </td>
            <td>:</td>
            <td>{peer?.tx}</td>
          </tr>
          <tr>
            <td>
              <b>Rx</b>
            </td>
            <td>:</td>
            <td>{peer?.rx}</td>
          </tr>
        </tbody>
      </table>
      <div className="flex flex-row justify-center space-x-8">
        <button
          className="bg-[#f2f4f7] rounded-sm hover:bg-[#e2e4e6]"
          onClick={() => downloadConfig("qr")}
        >
          <img
            src={QrIcon}
            alt="QR code"
            className="size-8 sm:size-9 lg:size-10 p-1 rounded-md"
          />
        </button>
        <button
          className="bg-[#f2f4f7] rounded-sm hover:bg-[#e2e4e6]"
          onClick={() => downloadConfig("file")}
        >
          <img
            src={DownloadIcon}
            alt="Download"
            className="size-8 sm:size-9 lg:size-10 p-1 rounded-md"
          />
        </button>
        <button
          className="bg-[#f2f4f7] rounded-sm hover:bg-[#e2e4e6]"
          onClick={deleteConfig}
        >
          <img
            src={DeleteIcon}
            alt="Delete"
            className="size-8 sm:size-9 lg:size-10 p-1 rounded-md"
          />
        </button>
      </div>
    </div>
  );
};

const PeerCards = ({ newPeerAdded, setNewPeerAdded }) => {
  const [peers, setPeers] = useState([]);
  const [error, setError] = useState(null);

  async function fetchPeers() {
    try {
      const response = await api.post("/peers");
      setPeers(response?.data);
      setError(null);
    } catch (error) {
      setError(error);
    }
  }

  useEffect(() => {
    if (newPeerAdded) {
      fetchPeers();
      setNewPeerAdded(false);
    }
  }, [newPeerAdded]);

  useEffect(() => {
    fetchPeers();
  }, []);

  return (
    <div className="grid justify-center grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3 lg:gap-6 md:gap-4 2xl:grid-cols-4 xl:gap-4 mb-6 mx-6">
      {error ? <div className="text-center">{error?.message}</div> : null}
      {peers.map((peer) => (
        <PeerCard
          peer={peer}
          fetchPeers={fetchPeers}
          key={peer?.id.toString()}
        />
      ))}
    </div>
  );
};

export default PeerCards;
